import {
  Customer,
  Contract,
  InventoryItem,
  Order,
  Invoice,
  Payment,
  Delivery,
  PriceData,
  ComplianceRecord,
  SalesMetrics,
  VoiceInvoiceData,
  ApiResponse,
  PaginatedResponse,
  CustomerFilters,
  OrderFilters,
  InventoryFilters
} from '../types';

// Default mock customers
const DEFAULT_CUSTOMERS: Customer[] = [
  {
    id: 'cust_001',
    name: 'Green Energy Industries',
    type: 'industrial',
    contactPerson: 'Rajesh Kumar',
    phone: '+91-9876543210',
    email: 'rajesh@greenenergy.com',
    address: {
      street: '123 Industrial Area, Phase 2',
      city: 'Noida',
      state: 'Uttar Pradesh',
      pincode: '201301',
      coordinates: { lat: 28.5355, lng: 77.3910 }
    },
    creditLimit: 5000000,
    creditUsed: 1250000,
    paymentTerms: 30,
    gstNumber: '09ABCDE1234F1Z5',
    panNumber: 'ABCDE1234F',
    status: 'active',
    registrationDate: new Date('2023-01-15'),
    lastOrderDate: new Date('2024-09-15'),
    totalOrders: 45,
    totalRevenue: 12500000,
    riskCategory: 'low'
  },
  {
    id: 'cust_002',
    name: 'Sunrise Hotel Chain',
    type: 'commercial',
    contactPerson: 'Priya Sharma',
    phone: '+91-9876543211',
    email: 'priya@sunrisehotels.com',
    address: {
      street: '45 MG Road',
      city: 'Bangalore',
      state: 'Karnataka',
      pincode: '560001',
      coordinates: { lat: 12.9716, lng: 77.5946 }
    },
    creditLimit: 1000000,
    creditUsed: 150000,
    paymentTerms: 15,
    gstNumber: '29FGHIJ5678K2L6',
    panNumber: 'FGHIJ5678K',
    status: 'active',
    registrationDate: new Date('2023-03-20'),
    lastOrderDate: new Date('2024-09-20'),
    totalOrders: 32,
    totalRevenue: 2800000,
    riskCategory: 'low'
  },
  {
    id: 'cust_003',
    name: 'Residential Society - Emerald Heights',
    type: 'residential',
    contactPerson: 'Amit Verma',
    phone: '+91-9876543212',
    email: 'amit@emeraldheights.com',
    address: {
      street: 'Emerald Heights, Sector 65',
      city: 'Gurgaon',
      state: 'Haryana',
      pincode: '122018',
      coordinates: { lat: 28.4089, lng: 77.0507 }
    },
    creditLimit: 500000,
    creditUsed: 75000,
    paymentTerms: 7,
    status: 'active',
    registrationDate: new Date('2023-06-10'),
    lastOrderDate: new Date('2024-09-18'),
    totalOrders: 28,
    totalRevenue: 850000,
    riskCategory: 'medium'
  }
];

// Storage keys
const STORAGE_KEY = 'saubhagya_customers';
const CONTRACTS_STORAGE_KEY = 'saubhagya_contracts';

// Initialize localStorage with default data if empty
const initializeStorage = (): void => {
  if (typeof window === 'undefined') return; // Skip in SSR

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_CUSTOMERS));
      console.log('Initialized localStorage with default customers');
    }

    // Clean contracts storage - remove any mock contracts like SB-2024-001
    try {
      const contractsStored = localStorage.getItem(CONTRACTS_STORAGE_KEY);
      if (contractsStored) {
        const contracts = JSON.parse(contractsStored);
        const filteredContracts = contracts.filter(
          (c: any) => !c.contractNumber.startsWith('SB-2024-')
        );
        localStorage.setItem(CONTRACTS_STORAGE_KEY, JSON.stringify(filteredContracts));
        console.log('Cleaned contracts storage:', { original: contracts.length, cleaned: filteredContracts.length });
      }
    } catch (parseError) {
      // If parse fails, just clear the contracts storage
      localStorage.setItem(CONTRACTS_STORAGE_KEY, JSON.stringify([]));
      console.log('Reset contracts storage due to parse error');
    }
  } catch (error) {
    console.error('Error initializing localStorage:', error);
  }
};

// Get customers from localStorage
const getStoredCustomers = (): Customer[] => {
  if (typeof window === 'undefined') return DEFAULT_CUSTOMERS; // Skip in SSR

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const customers = JSON.parse(stored);
      // Convert date strings back to Date objects
      return customers.map((c: any) => ({
        ...c,
        registrationDate: new Date(c.registrationDate),
        lastOrderDate: c.lastOrderDate ? new Date(c.lastOrderDate) : undefined
      }));
    }
  } catch (error) {
    console.error('Error reading from localStorage:', error);
  }
  return DEFAULT_CUSTOMERS;
};

// Save customers to localStorage
const saveStoredCustomers = (customers: Customer[]): void => {
  if (typeof window === 'undefined') return; // Skip in SSR

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(customers));
    console.log('Customers saved to localStorage:', customers.length);
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

// Get contracts from localStorage
const getStoredContracts = (): Contract[] => {
  if (typeof window === 'undefined') return generateMockContracts(); // Skip in SSR

  try {
    const stored = localStorage.getItem(CONTRACTS_STORAGE_KEY);
    if (stored) {
      const contracts = JSON.parse(stored);
      // Convert date strings back to Date objects
      return contracts.map((c: any) => ({
        ...c,
        startDate: new Date(c.startDate),
        endDate: new Date(c.endDate),
        signedDate: c.signedDate ? new Date(c.signedDate) : undefined
      }));
    }
  } catch (error) {
    console.error('Error reading contracts from localStorage:', error);
  }
  return generateMockContracts();
};

// Save contracts to localStorage
const saveStoredContracts = (contracts: Contract[]): void => {
  if (typeof window === 'undefined') return; // Skip in SSR

  try {
    localStorage.setItem(CONTRACTS_STORAGE_KEY, JSON.stringify(contracts));
    console.log('Contracts saved to localStorage:', contracts.length);
  } catch (error) {
    console.error('Error saving contracts to localStorage:', error);
  }
};

// Initialize storage on module load
initializeStorage();

// Mock data generators
const generateMockCustomers = (): Customer[] => getStoredCustomers();

const generateMockContracts = (): Contract[] => [];

const generateMockInventory = (): InventoryItem[] => {
  const baseMockInventory: InventoryItem[] = [
    {
      id: 'inv_001',
      gasType: 'biogas',
      batchNumber: 'BG-2024-001',
      productionDate: new Date('2024-09-15'),
      quantity: 5000,
      availableQuantity: 3500,
      unit: 'cubic_meters',
      quality: {
        methaneContent: 65,
        purity: 98.5,
        pressure: 15,
        temperature: 25
      },
      location: {
        plantId: 'plant_001',
        tankId: 'tank_A1',
        coordinates: { lat: 28.7041, lng: 77.1025 }
      },
      pesoCompliance: {
        certificateNumber: 'PESO-2024-001',
        issueDate: new Date('2024-01-15'),
        expiryDate: new Date('2025-01-15'),
        status: 'valid',
        inspector: 'Dr. Rajesh Gupta'
      },
      pricePerUnit: 45,
      reservedQuantity: 1500,
      status: 'available'
    },
    {
      id: 'inv_002',
      gasType: 'compressed',
      batchNumber: 'CG-2024-005',
      productionDate: new Date('2024-09-20'),
      quantity: 2000,
      availableQuantity: 1800,
      unit: 'kg',
      quality: {
        methaneContent: 95,
        purity: 99.2,
        pressure: 200,
        temperature: 20
      },
      location: {
        plantId: 'plant_002',
        tankId: 'tank_B2',
        coordinates: { lat: 28.6139, lng: 77.2090 }
      },
      pesoCompliance: {
        certificateNumber: 'PESO-2024-002',
        issueDate: new Date('2024-02-01'),
        expiryDate: new Date('2025-02-01'),
        status: 'valid',
        inspector: 'Mr. Suresh Yadav'
      },
      pricePerUnit: 65,
      reservedQuantity: 200,
      status: 'available'
    }
  ];

  // Get demo batches from localStorage and convert to InventoryItem format
  try {
    const demoBatchesJson = localStorage.getItem('saubhagya_demo_batches');
    if (demoBatchesJson) {
      const demoBatches = JSON.parse(demoBatchesJson);
      // Convert BatchResponse objects to InventoryItem format
      const convertedDemoBatches: InventoryItem[] = demoBatches.map((batch: any, index: number) => ({
        id: `inv_demo_${index}_${batch.id}`,
        gasType: batch.productType.toLowerCase().replace(/_/g, ' '),
        batchNumber: batch.batchNumber,
        productionDate: new Date(batch.productionDate),
        quantity: batch.initialQuantity,
        availableQuantity: batch.availableQuantity,
        unit: batch.quantityUnit.toLowerCase().replace(/_/g, ' '),
        quality: {
          methaneContent: 65,
          purity: batch.purityPercentage,
          pressure: 15,
          temperature: 25
        },
        location: {
          plantId: batch.storageLocation.split('-')[0].trim(),
          tankId: batch.storageLocation.split('-')[1]?.trim() || 'Unknown',
          coordinates: { lat: 28.7041, lng: 77.1025 }
        },
        pesoCompliance: {
          certificateNumber: batch.pesoCertificate?.number || `PESO-${new Date().getFullYear()}-TEMP`,
          issueDate: new Date(batch.productionDate),
          expiryDate: new Date(batch.expiryDate),
          status: batch.pesoCompliant ? 'valid' : 'pending',
          inspector: batch.pesoCertificate?.inspector || 'Pending'
        },
        pricePerUnit: 50,
        reservedQuantity: batch.allocatedQuantity,
        status: batch.batchStatus.toLowerCase() === 'available' ? 'available' : batch.batchStatus.toLowerCase()
      }));

      console.log('Loaded demo batches from localStorage:', convertedDemoBatches.length);
      return [...baseMockInventory, ...convertedDemoBatches];
    }
  } catch (error) {
    console.error('Error loading demo batches from localStorage:', error);
  }

  return baseMockInventory;
};

const generateMockOrders = (): Order[] => [
  {
    id: 'ord_001',
    orderNumber: 'ORD-2024-001',
    customerId: 'cust_001',
    contractId: 'cont_001',
    orderDate: new Date('2024-09-15'),
    requestedDeliveryDate: new Date('2024-09-25'),
    status: 'confirmed',
    items: [
      {
        inventoryId: 'inv_001',
        gasType: 'biogas',
        quantity: 1000,
        pricePerUnit: 45,
        totalAmount: 45000,
        batchNumber: 'BG-2024-001'
      }
    ],
    totalQuantity: 1000,
    totalAmount: 45000,
    discountPercentage: 5,
    discountAmount: 2250,
    taxAmount: 7695,
    finalAmount: 50445,
    deliveryAddress: {
      street: '123 Industrial Area, Phase 2',
      city: 'Noida',
      state: 'Uttar Pradesh',
      pincode: '201301',
      coordinates: { lat: 28.5355, lng: 77.3910 }
    },
    deliveryInstructions: 'Delivery during business hours only',
    invoiceGenerated: true,
    invoiceNumber: 'INV-2024-001',
    paymentStatus: 'pending',
    createdBy: 'sales_user_001',
    lastModifiedBy: 'sales_user_001',
    lastModifiedDate: new Date('2024-09-15')
  }
];

const generateMockInvoices = (): Invoice[] => [
  {
    id: 'inv_001',
    invoiceNumber: 'INV-2024-001',
    orderId: 'ord_001',
    customerId: 'cust_001',
    invoiceDate: new Date('2024-09-15'),
    dueDate: new Date('2024-10-15'),
    status: 'sent',
    items: [
      {
        description: 'Biogas Supply - BG-2024-001',
        quantity: 1000,
        unit: 'cubic meters',
        pricePerUnit: 45,
        totalAmount: 45000,
        taxRate: 18,
        taxAmount: 8100
      }
    ],
    subtotal: 45000,
    discountAmount: 2250,
    taxAmount: 7695,
    totalAmount: 50445,
    paidAmount: 0,
    balanceAmount: 50445,
    paymentTerms: 'Net 30 days',
    bankDetails: {
      accountName: 'Saubhagya Energy Solutions',
      accountNumber: '1234567890',
      ifscCode: 'SBIN0001234',
      bankName: 'State Bank of India'
    },
    notes: 'Payment due within 30 days of invoice date',
    generatedBy: 'manual'
  }
];

// Service functions
export const salesService = {
  // Customer Management
  async getCustomers(filters?: CustomerFilters): Promise<PaginatedResponse<Customer>> {
    let customers = generateMockCustomers();

    // Apply filters if provided
    if (filters) {
      // Filter by type
      if (filters.type && filters.type.length > 0) {
        customers = customers.filter(c => filters.type!.includes(c.type));
      }

      // Filter by status
      if (filters.status && filters.status.length > 0) {
        customers = customers.filter(c => filters.status!.includes(c.status));
      }

      // Filter by risk category
      if (filters.riskCategory && filters.riskCategory.length > 0) {
        customers = customers.filter(c => filters.riskCategory!.includes(c.riskCategory));
      }

      // Filter by city
      if (filters.city && filters.city.length > 0) {
        customers = customers.filter(c => filters.city!.includes(c.address.city));
      }

      // Filter by credit range
      if (filters.creditRange) {
        customers = customers.filter(c =>
          c.creditLimit >= filters.creditRange!.min &&
          c.creditLimit <= filters.creditRange!.max
        );
      }

      // Filter by registration date range
      if (filters.registrationDateRange) {
        const { start, end } = filters.registrationDateRange;
        customers = customers.filter(c => {
          const regDate = new Date(c.registrationDate);
          return regDate >= start && regDate <= end;
        });
      }
    }

    console.log('Filtered customers:', customers.length, 'Applied filters:', filters);

    return {
      success: true,
      data: customers,
      pagination: {
        page: 1,
        limit: 10,
        total: customers.length,
        totalPages: 1
      }
    };
  },

  async getCustomerById(id: string): Promise<ApiResponse<Customer>> {
    const customers = generateMockCustomers();
    const customer = customers.find(c => c.id === id);
    return {
      success: !!customer,
      data: customer!,
      message: customer ? 'Customer found' : 'Customer not found'
    };
  },

  async createCustomer(customer: Omit<Customer, 'id'>): Promise<ApiResponse<Customer>> {
    // Add default values for missing fields
    const newCustomer: Customer = {
      ...customer,
      id: `cust_${Date.now()}`,
      registrationDate: customer.registrationDate || new Date(),
      totalOrders: customer.totalOrders || 0,
      totalRevenue: customer.totalRevenue || 0,
      riskCategory: customer.riskCategory || 'medium',
      creditUsed: customer.creditUsed || 0,
      lastOrderDate: customer.lastOrderDate || new Date()
    };

    // Get current customers and add new one
    const customers = getStoredCustomers();
    customers.push(newCustomer);

    // Save to localStorage (persists across page refreshes)
    saveStoredCustomers(customers);
    console.log('Customer created and persisted to localStorage:', newCustomer);

    return {
      success: true,
      data: newCustomer,
      message: 'Customer created successfully'
    };
  },

  async updateCustomer(id: string, updates: Partial<Customer>): Promise<ApiResponse<Customer>> {
    const customers = getStoredCustomers();
    const customerIndex = customers.findIndex(c => c.id === id);

    if (customerIndex === -1) {
      return { success: false, data: null as any, message: 'Customer not found' };
    }

    const updatedCustomer = { ...customers[customerIndex], ...updates };

    // Update in customers array
    customers[customerIndex] = updatedCustomer;

    // Save to localStorage (persists across page refreshes)
    saveStoredCustomers(customers);
    console.log('Customer updated and persisted to localStorage:', updatedCustomer);

    return {
      success: true,
      data: updatedCustomer,
      message: 'Customer updated successfully'
    };
  },

  async checkEmailUniquenessForUpdate(email: string, customerId: string): Promise<boolean> {
    const customers = generateMockCustomers();
    const existingCustomer = customers.find(
      c => c.email.toLowerCase() === email.toLowerCase() && c.id !== customerId
    );
    return !existingCustomer; // Returns true if email is unique
  },

  async checkPhoneUniquenessForUpdate(phone: string, customerId: string): Promise<boolean> {
    const customers = generateMockCustomers();
    const existingCustomer = customers.find(
      c => c.phone === phone && c.id !== customerId
    );
    return !existingCustomer; // Returns true if phone is unique
  },

  async checkGstUniquenessForUpdate(gst: string, customerId: string): Promise<boolean> {
    if (!gst) return true; // GST is optional
    const customers = generateMockCustomers();
    const existingCustomer = customers.find(
      c => c.gstNumber === gst && c.id !== customerId
    );
    return !existingCustomer; // Returns true if GST is unique
  },

  // Contract Management
  async getContracts(): Promise<PaginatedResponse<Contract>> {
    const contracts = getStoredContracts();
    return {
      success: true,
      data: contracts,
      pagination: {
        page: 1,
        limit: 10,
        total: contracts.length,
        totalPages: 1
      }
    };
  },

  async getContractById(id: string): Promise<ApiResponse<Contract>> {
    const contracts = getStoredContracts();
    const contract = contracts.find(c => c.id === id);
    return {
      success: !!contract,
      data: contract!,
      message: contract ? 'Contract found' : 'Contract not found'
    };
  },

  async createContract(contract: Omit<Contract, 'id'>): Promise<ApiResponse<Contract>> {
    const newContract: Contract = {
      ...contract,
      id: `cont_${Date.now()}`
    };

    // Get existing contracts and add new one
    const contracts = getStoredContracts();
    contracts.push(newContract);

    // Save to localStorage (persists across page refreshes)
    saveStoredContracts(contracts);
    console.log('Contract created and persisted to localStorage:', newContract);

    return {
      success: true,
      data: newContract,
      message: 'Contract created successfully'
    };
  },

  async updateContract(id: string, updates: Partial<Contract>): Promise<ApiResponse<Contract>> {
    const contracts = getStoredContracts();
    const contractIndex = contracts.findIndex(c => c.id === id);

    if (contractIndex === -1) {
      return { success: false, data: null as any, message: 'Contract not found' };
    }

    const updatedContract = { ...contracts[contractIndex], ...updates };

    // Update in contracts array
    contracts[contractIndex] = updatedContract;

    // Save to localStorage (persists across page refreshes)
    saveStoredContracts(contracts);
    console.log('Contract updated and persisted to localStorage:', updatedContract);

    return {
      success: true,
      data: updatedContract,
      message: 'Contract updated successfully'
    };
  },

  // Inventory Management
  async getInventory(filters?: InventoryFilters): Promise<PaginatedResponse<InventoryItem>> {
    const inventory = generateMockInventory();
    return {
      success: true,
      data: inventory,
      pagination: {
        page: 1,
        limit: 10,
        total: inventory.length,
        totalPages: 1
      }
    };
  },

  async updateInventoryItem(id: string, updates: Partial<InventoryItem>): Promise<ApiResponse<InventoryItem>> {
    const inventory = generateMockInventory();
    const item = inventory.find(i => i.id === id);
    if (!item) {
      return { success: false, data: null as any, message: 'Inventory item not found' };
    }
    const updatedItem = { ...item, ...updates };
    return {
      success: true,
      data: updatedItem,
      message: 'Inventory item updated successfully'
    };
  },

  // Order Management
  async getOrders(filters?: OrderFilters): Promise<PaginatedResponse<Order>> {
    const orders = generateMockOrders();
    return {
      success: true,
      data: orders,
      pagination: {
        page: 1,
        limit: 10,
        total: orders.length,
        totalPages: 1
      }
    };
  },

  async createOrder(order: Omit<Order, 'id' | 'orderNumber'>): Promise<ApiResponse<Order>> {
    const newOrder: Order = {
      ...order,
      id: `ord_${Date.now()}`,
      orderNumber: `ORD-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`
    };
    return {
      success: true,
      data: newOrder,
      message: 'Order created successfully'
    };
  },

  // Invoice Management
  async getInvoices(): Promise<PaginatedResponse<Invoice>> {
    const invoices = generateMockInvoices();
    return {
      success: true,
      data: invoices,
      pagination: {
        page: 1,
        limit: 10,
        total: invoices.length,
        totalPages: 1
      }
    };
  },

  async generateInvoice(orderId: string): Promise<ApiResponse<Invoice>> {
    const invoice: Invoice = {
      id: `inv_${Date.now()}`,
      invoiceNumber: `INV-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`,
      orderId,
      customerId: 'cust_001',
      invoiceDate: new Date(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      status: 'draft',
      items: [],
      subtotal: 0,
      discountAmount: 0,
      taxAmount: 0,
      totalAmount: 0,
      paidAmount: 0,
      balanceAmount: 0,
      paymentTerms: 'Net 30 days',
      bankDetails: {
        accountName: 'Saubhagya Energy Solutions',
        accountNumber: '1234567890',
        ifscCode: 'SBIN0001234',
        bankName: 'State Bank of India'
      },
      generatedBy: 'manual'
    };
    return {
      success: true,
      data: invoice,
      message: 'Invoice generated successfully'
    };
  },

  // Voice Invoicing
  async startVoiceSession(language: 'hindi' | 'english'): Promise<ApiResponse<VoiceInvoiceData>> {
    const session: VoiceInvoiceData = {
      id: `voice_${Date.now()}`,
      sessionId: `session_${Date.now()}`,
      startTime: new Date(),
      language,
      transcription: '',
      confidence: 0,
      extractedData: { items: [] },
      status: 'recording',
      validationErrors: []
    };
    return {
      success: true,
      data: session,
      message: 'Voice session started'
    };
  },

  async processVoiceData(sessionId: string, audioData: string): Promise<ApiResponse<VoiceInvoiceData>> {
    // Mock processing
    const processedData: VoiceInvoiceData = {
      id: sessionId,
      sessionId,
      startTime: new Date(Date.now() - 60000),
      endTime: new Date(),
      language: 'hindi',
      transcription: 'ग्रीन एनर्जी इंडस्ट्रीज के लिए एक हजार क्यूबिक मीटर बायो गैस, पैंतालीस रुपए प्रति यूनिट',
      confidence: 0.85,
      extractedData: {
        customerName: 'Green Energy Industries',
        customerId: 'cust_001',
        items: [
          {
            description: 'Biogas',
            quantity: 1000,
            unit: 'cubic meters',
            pricePerUnit: 45
          }
        ],
        totalAmount: 45000
      },
      status: 'completed',
      validationErrors: []
    };
    return {
      success: true,
      data: processedData,
      message: 'Voice data processed successfully'
    };
  },

  // Payment Management
  async getPayments(): Promise<PaginatedResponse<Payment>> {
    const payments: Payment[] = [];
    return {
      success: true,
      data: payments,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      }
    };
  },

  // Delivery Management
  async getDeliveries(): Promise<PaginatedResponse<Delivery>> {
    const deliveries: Delivery[] = [];
    return {
      success: true,
      data: deliveries,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      }
    };
  },

  // Price Management
  async getPriceData(): Promise<ApiResponse<PriceData[]>> {
    const priceData: PriceData[] = [];
    return {
      success: true,
      data: priceData,
      message: 'Price data retrieved successfully'
    };
  },

  // Compliance
  async getComplianceRecords(): Promise<PaginatedResponse<ComplianceRecord>> {
    const records: ComplianceRecord[] = [];
    return {
      success: true,
      data: records,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      }
    };
  },

  // Order Confirmation (US-ORD-003)
  async validateOrderForConfirmation(orderId: string): Promise<any> {
    const API_BASE_URL = process.env.REACT_APP_COMMERCE_API_URL || 'http://localhost:8083/commerce-service';
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/orders/${orderId}/validate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Validation failed' }));
        throw new Error(error.message || `HTTP Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error validating order for confirmation:', error);
      throw error;
    }
  },

  async confirmOrder(orderId: string, confirmedBy: string = 'current_user'): Promise<any> {
    const API_BASE_URL = process.env.REACT_APP_COMMERCE_API_URL || 'http://localhost:8083/commerce-service';
    const token = localStorage.getItem('saubhagya_jwt_token') || '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/orders/${orderId}/confirm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ confirmedBy }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Order confirmation failed' }));

        // Handle specific error codes
        if (response.status === 409) {
          throw new Error(error.message || 'Inventory or credit unavailable. Please check order details.');
        } else if (response.status === 402) {
          throw new Error(error.message || 'Payment required. Insufficient credit.');
        } else if (response.status === 404) {
          throw new Error(error.message || 'Order not found.');
        } else if (response.status === 400) {
          throw new Error(error.message || 'Invalid order status. Only PENDING orders can be confirmed.');
        }

        throw new Error(error.message || `HTTP Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error confirming order:', error);
      throw error;
    }
  },

  async getInvoicePreview(invoiceId: string): Promise<any> {
    // This would call backend API: GET /api/v1/invoices/{id}/preview
    return {
      invoiceNumber: 'INV-2024-000123',
      invoiceDate: new Date(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      orderNumber: 'ORD-2024-001',
      customerName: 'Green Energy Industries',
      customerAddress: '123 Industrial Area, Phase 2, Noida, UP 201301',
      customerGST: '09ABCDE1234F1Z5',
      items: [
        {
          description: 'Biogas Supply - BG-2024-001',
          quantity: 1000,
          unit: 'cubic meters',
          pricePerUnit: 45,
          amount: 45000,
        },
      ],
      subtotal: 45000,
      discountAmount: 2250,
      taxableAmount: 42750,
      cgst: 3847.50,
      sgst: 3847.50,
      igst: 0,
      totalAmount: 50445,
      paymentTerms: 'Net 30 days',
      notes: 'Payment due within 30 days of invoice date',
    };
  },

  // Dashboard Metrics
  async getSalesMetrics(): Promise<ApiResponse<SalesMetrics>> {
    const metrics: SalesMetrics = {
      totalRevenue: 15000000,
      totalOrders: 105,
      averageOrderValue: 142857,
      totalCustomers: 25,
      activeContracts: 8,
      inventoryValue: 2500000,
      pendingPayments: 750000,
      deliverySuccess: 98.5,
      customerSatisfaction: 4.6,
      periodComparison: {
        revenue: { current: 15000000, previous: 12000000, change: 25 },
        orders: { current: 105, previous: 85, change: 23.5 },
        customers: { current: 25, previous: 20, change: 25 }
      }
    };
    return {
      success: true,
      data: metrics,
      message: 'Metrics retrieved successfully'
    };
  }
};

export default salesService;